import { useCallback, useRef } from "react";

import { MESSAGE_SCROLL_PADDING_Y } from "../components/styles";

import { useElementMaxHeightRef } from "./useElementMaxHeightRef";
import { useHasUserScrolled } from "./useHasUserScrolled";

type HookParams = {
    /* Messages list scroll container reference */
    containerRef: React.RefObject<HTMLDivElement | null>;

    lastUserMessageRef: React.RefObject<HTMLDivElement | null>;

    lastAiMessageRef: React.RefObject<HTMLDivElement | null>;

    bottomRef: React.RefObject<HTMLDivElement | null>;
};

type HookValue = {
    scrollChatWhenAiResponse: VoidFunction;
    scrollUserMessageToTop: (bottomOffset?: number, topOffset?: number) => void;
    scrollToBottom: (behavior?: ScrollBehavior) => void;
    removeSpacer: VoidFunction;
};

export const useChatScrolling = ({
    containerRef,
    lastUserMessageRef,
    lastAiMessageRef,
    bottomRef,
}: HookParams): HookValue => {
    const spacerRef = useRef<HTMLDivElement | null>(null);

    const userScrolledRef = useHasUserScrolled(containerRef);
    const maxContainerHeightRef = useElementMaxHeightRef(containerRef);

    const scrollToBottom = useCallback((behavior: ScrollBehavior = "instant") => {
        bottomRef.current?.scrollIntoView({ behavior, block: "end" });
    }, []);

    const scrollUserMessageToTop = useCallback((bottomOffset: number = 0, topOffset?: number) => {
        const scrollTopPadding = topOffset ?? MESSAGE_SCROLL_PADDING_Y;
        if (!lastUserMessageRef.current || !containerRef.current) {
            return;
        }

        const container = containerRef.current;
        const message = lastUserMessageRef.current;

        if (spacerRef.current) {
            spacerRef.current.remove();
            spacerRef.current = null;
        }

        const containerRect = container.getBoundingClientRect();
        const messageRect = message.getBoundingClientRect();

        const spacer = document.createElement("div");
        spacerRef.current = spacer;
        // Use the maximum height for consistent scroll positioning
        spacer.style.minHeight = `${Math.max(0, maxContainerHeightRef.current - bottomOffset - message.clientHeight)}px`;
        container.appendChild(spacer);

        const messageTop = messageRect.top - scrollTopPadding;
        const top = messageTop - containerRect.top + container.scrollTop;

        container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, []);

    const scrollChatWhenAiResponse = useCallback(() => {
        if (containerRef.current && lastUserMessageRef.current && lastAiMessageRef.current) {
            const container = containerRef.current;
            const userMessage = lastUserMessageRef.current;
            const aiMessage = lastAiMessageRef.current;

            const aiMessageTallerThanView =
                aiMessage.clientHeight > container.clientHeight - userMessage.scrollHeight - 52;

            if (aiMessageTallerThanView && spacerRef.current) {
                spacerRef.current.remove();
                spacerRef.current = null;

                requestAnimationFrame(() => scrollToBottom());
            }

            if (userScrolledRef.current) {
                return;
            }

            if (!spacerRef.current) {
                requestAnimationFrame(() => scrollToBottom());
            }
        }
    }, [scrollToBottom]);

    const removeSpacer = useCallback(() => {
        if (spacerRef.current) {
            spacerRef.current.remove();
            spacerRef.current = null;
        }
    }, []);

    return {
        scrollChatWhenAiResponse,
        scrollUserMessageToTop,
        scrollToBottom,
        removeSpacer,
    };
};
